<script>
/**
 * Casca do painel lateral de IA: superfície fixa à direita e, no lugar dela
 * quando recolhido, uma aba fina colada na borda.
 *
 * É só a casca — quem decide o que vai dentro é o consumidor, pelo slot. O
 * primeiro é o Consultor Estratégico do AAWZ Hub, que põe o `ViewChat` aqui.
 *
 * Não é `v-navigation-drawer`: o design-system proíbe usar drawer como
 * substituto de modal (`design-system/guidelines.md:86`), e este painel empurra
 * o conteúdo em vez de sobrepô-lo — não é modal nem navegação.
 *
 * O SQUEEZE não mora aqui. Quem desloca o conteúdo é a raiz do layout
 * (`main.vue`, classe `has-ia-painel`), porque o `padding-right` precisa ser
 * aplicado no ancestral de tudo. A anatomia visual mora em
 * `modules/global/design/_ia-painel-lateral.sass`, partial global.
 */
export default {
  name: 'IaPainelLateral',
  props: {
    // Aberto = superfície visível. Fechado = aba fina. O estado é do
    // consumidor (Vuex), nunca local: o `RouterView` é chaveado pelo path
    // (`app.vue`), então um flag local morreria a cada navegação.
    aberto: {
      type: Boolean,
      default: false,
    },
    titulo: {
      type: String,
      required: true,
    },
    // Linha de apoio ao lado do título — o popup usa para dizer em que modo
    // está ("Histórico do chat"). Vazio não renderiza nada.
    subtitulo: {
      type: String,
      default: '',
    },
    // Rótulo vertical da aba recolhida. Curto — a aba tem 40px de largura.
    rotuloAba: {
      type: String,
      required: true,
    },
  },
  computed: {
    rotuloAbrir() {
      return 'Abrir ' + this.titulo
    },
    rotuloRecolher() {
      return 'Recolher ' + this.titulo
    },
  },
  methods: {
    abrir() {
      this.$emit('abrir')
    },
    fechar() {
      this.$emit('fechar')
    },
  },
}
</script>

<template>
  <!-- `v-application` na raiz: os painéis são montados FORA do `.v-application`
       do layout (precisam sobreviver à `key` do RouterView), e o Vuetify 2
       escopa TODAS as utilitárias sob essa classe — `d-flex`, `flex-column`,
       `pa-4`. Sem ela, um conteúdo que use utilitárias (o `ViewChat` usa muito)
       renderiza com o layout desmontado: tudo vira `flex-direction: row`.
       Mesmo remédio do `#aawz-floating-dock` em `main.vue`. -->
  <div class="ia-painel-lateral v-application">
    <transition name="ia-painel-slide">
      <aside
        v-if="aberto"
        class="ia-painel-lateral__superficie"
        :aria-label="titulo"
      >
        <header class="ia-painel-lateral__topo">
          <!-- Mesma identidade do cabeçalho do popup: avatar claro com o "W" da
               wordmark em verde institucional, título e subtítulo empilhados ao
               lado. As duas superfícies são a mesma conversa — o cabeçalho é o
               que o gestor usa para reconhecê-la. -->
          <div class="ia-painel-lateral__marca">
            <!-- Slot com default: o avatar é a identidade da família de chats,
                 então vem de graça; quem tiver motivo para outro sinal (ou
                 nenhum) sobrescreve sem precisar de prop booleana. -->
            <slot name="marca">
              <div class="ia-painel-lateral__avatar">
                <icon-svg
                  name="ia-w-mark"
                  :size="20"
                  color="var(--aawz-green-color)"
                />
              </div>
            </slot>
            <div class="ia-painel-lateral__texto">
              <div class="ia-painel-lateral__titulo">{{ titulo }}</div>
              <div
                v-if="subtitulo"
                class="ia-painel-lateral__subtitulo"
              >{{ subtitulo }}</div>
            </div>
          </div>

          <div class="d-flex align-center ia-painel-lateral__acoes">
            <!-- Ações do consumidor (nova conversa, etc.) antes do recolher:
                 o botão de fechar é sempre o último da fileira. -->
            <slot name="acoes" />

            <button
              type="button"
              class="ia-painel-lateral__botao"
              :aria-label="rotuloRecolher"
              :title="rotuloRecolher"
              @click="fechar"
            >
              <!-- Não existe `arrow-right-*` nativo; o chevron dos blocos do
                   chat é rotacionado pelo mesmo motivo (`ia-block-seta`). Nome
                   de ícone inexistente renderiza VAZIO, sem erro. -->
              <icon-svg
                name="arrow-down-new-ux"
                :size="16"
                color="var(--neutral-700)"
                class="ia-painel-lateral__seta--direita"
              />
            </button>
          </div>
        </header>

        <div class="ia-painel-lateral__corpo">
          <slot />
        </div>
      </aside>
    </transition>

    <transition name="ia-painel-aba-fade">
      <button
        v-if="!aberto"
        type="button"
        class="ia-painel-lateral__aba"
        :aria-label="rotuloAbrir"
        :title="titulo"
        @click="abrir"
      >
        <icon-svg
          name="arrow-down-new-ux"
          :size="16"
          color="var(--white)"
          class="ia-painel-lateral__seta--esquerda"
        />
        <span class="ia-painel-lateral__aba-rotulo">{{ rotuloAba }}</span>
      </button>
    </transition>
  </div>
</template>

<style lang="sass" scoped>
// A anatomia inteira mora na partial global
// `modules/global/design/_ia-painel-lateral.sass` — inclusive os botões do
// cabeçalho, porque eles vêm por SLOT (ver o porquê lá). Aqui fica só a rotação
// dos chevrons, que é do template desta casca.
//
// A raiz não precisa de CSS: os dois filhos são `position: fixed`, ancorados na
// viewport, e não dependem de containing block deste elemento.
// Os dois sentidos que faltam no catálogo de ícones, derivados do chevron para
// baixo — mesma solução do `ia-block-seta` dos blocos do chat.
// Empilhado sob o título, como no popup — não mais ao lado dele.
.ia-painel-lateral__subtitulo
  font-size: 11px
  line-height: 1.3
  color: var(--neutral-700)

.ia-painel-lateral__seta--direita
  transform: rotate(-90deg)

.ia-painel-lateral__seta--esquerda
  transform: rotate(90deg)
</style>
